import { freshNotebook } from './notebook.ts';
import type { Notebook, SavedNotebook } from './notebook.ts';
import { RevisionConflict } from './progress-client.ts';
import type { ProgressClient } from './progress-client.ts';
export type SessionSnapshot = {
  document: Notebook;
  status: 'saved' | 'saving' | 'error';
  message: string;
  generation: number;
};

/** One session belongs to exactly one username. All writes run in a single queue. */
export class ProgressSession {
  private client: ProgressClient;
  private revision: number | null;
  private dirty = false;
  private timer?: ReturnType<typeof setTimeout>;
  private running?: Promise<boolean>;
  private listeners = new Set<() => void>();
  private snapshot: SessionSnapshot;
  constructor(client: ProgressClient, saved: SavedNotebook | null) {
    this.client = client;
    this.revision = saved?.revision ?? null;
    this.snapshot = {
      document: saved?.document ?? freshNotebook(),
      status: 'saved',
      message: '',
      generation: 0,
    };
  }
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private publish(values: Partial<SessionSnapshot>) {
    this.snapshot = { ...this.snapshot, ...values };
    this.listeners.forEach((fn) => fn());
  }
  edit(update: (document: Notebook) => Notebook, remount = false) {
    const document = update(this.snapshot.document);
    if (document === this.snapshot.document) return;
    this.dirty = true;
    this.publish({
      document,
      status: 'saving',
      message: '',
      generation: this.snapshot.generation + Number(remount),
    });
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.flush();
    }, 350);
  }
  hasPending = () => this.dirty || !!this.running;
  flush = (): Promise<boolean> => {
    clearTimeout(this.timer);
    if (this.running) return this.running;
    this.running = this.drain().finally(() => {
      this.running = undefined;
    });
    return this.running;
  };
  private async drain(): Promise<boolean> {
    while (this.dirty) {
      const document = this.snapshot.document;
      this.publish({ status: 'saving', message: '' });
      try {
        const saved = await this.client.save(document, this.revision);
        this.revision = saved.revision;
        if (this.snapshot.document === document) this.dirty = false;
      } catch (error) {
        if (error instanceof RevisionConflict) {
          try {
            const latest = await this.client.get();
            if (!latest) throw new Error('Notebook disappeared');
            // Also handles a successful save whose HTTP response was lost.
            if (JSON.stringify(latest.document) === JSON.stringify(document)) {
              this.revision = latest.revision;
              if (this.snapshot.document === document) this.dirty = false;
              continue;
            }
            this.revision = latest.revision;
            this.dirty = false;
            this.publish({
              document: latest.document,
              status: 'saved',
              generation: this.snapshot.generation + 1,
              message:
                'Progress changed in another tab or device. Loaded that version; your unsaved changes here were not applied.',
            });
            return false;
          } catch {
            /* Keep pending work and retry; never replace unreadable data. */
          }
        }
        this.publish({
          status: 'error',
          message: 'Changes are not saved yet. Keep this tab open and retry.',
        });
        return false;
      }
    }
    this.publish({ status: 'saved' });
    return true;
  }
  async refresh() {
    if (this.hasPending()) return;
    const revision = this.revision;
    try {
      const latest = await this.client.get();
      if (this.hasPending() || revision !== this.revision) return;
      if (latest && latest.revision !== revision) {
        this.revision = latest.revision;
        this.publish({
          document: latest.document,
          generation: this.snapshot.generation + 1,
          status: 'saved',
          message: 'Loaded progress from another tab or device.',
        });
      }
    } catch {
      this.publish({
        message: 'Could not check for changes on other devices. Retry when connected.',
      });
    }
  }
}
